import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Mail, Save, KeyRound, Phone, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { clearSettingsCache } from "@/lib/settings";

const EMPTY_SETTINGS = {
    instagram_url: "",
    alert_email: "",
    whatsapp_primary: "",
    whatsapp_secondary: "",
    contact_email: "",
    cadastur_number: "",
    cadastur_url: "",
};

/**
 * Configurações do portal: canais de atendimento (WhatsApp, Instagram,
 * e-mail corporativo), identificação Cadastur e e-mail de alertas.
 * Tudo editável sem alterar código; o site inteiro reflete automaticamente.
 */
export default function AdminSettings() {
    const [form, setForm] = useState(EMPTY_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/api/admin/settings")
            .then(({ data }) => setForm({ ...EMPTY_SETTINGS, ...data }))
            .catch(() => toast.error("Não foi possível carregar as configurações."))
            .finally(() => setLoading(false));
    }, []);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/api/admin/settings", form);
            clearSettingsCache();
            toast.success("Configurações salvas. O site inteiro já reflete as alterações.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Não foi possível salvar.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="mt-10 flex justify-center" data-testid="settings-loading">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-forest-emerald border-t-transparent" />
            </div>
        );
    }

    return (
        <>
        <form onSubmit={handleSave} className="mt-8 max-w-2xl rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm sm:p-8" data-testid="admin-settings">
            <h2 className="font-serif text-xl font-semibold text-forest-deep">Configurações do portal</h2>

            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-wa-primary">
                        <Phone className="h-3.5 w-3.5 text-gold-hover" />
                        WhatsApp principal (cotações & vendas)
                    </label>
                    <input id="set-wa-primary" data-testid="settings-wa-primary-input" value={form.whatsapp_primary} onChange={set("whatsapp_primary")} className="field-input mt-1.5" placeholder="(47) 99162-4421" />
                </div>
                <div>
                    <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-wa-secondary">
                        <Phone className="h-3.5 w-3.5 text-gold-hover" />
                        WhatsApp alternativo
                    </label>
                    <input id="set-wa-secondary" data-testid="settings-wa-secondary-input" value={form.whatsapp_secondary} onChange={set("whatsapp_secondary")} className="field-input mt-1.5" placeholder="(47) 98925-1767" />
                </div>
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400">
                Estes números alimentam automaticamente todos os botões e links de WhatsApp do site, do formulário e do consultor virtual.
            </p>

            <div className="mt-6">
                <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-contact-email">
                    <Mail className="h-3.5 w-3.5 text-gold-hover" />
                    E-mail corporativo (exibido no rodapé)
                </label>
                <input id="set-contact-email" data-testid="settings-contact-email-input" type="email" value={form.contact_email} onChange={set("contact_email")} className="field-input mt-1.5" placeholder="contato@destinoresorts.com.br" />
            </div>

            <div className="mt-7 border-t border-[#E5DEC9] pt-6">
                <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                    <BadgeCheck className="h-4 w-4 text-gold-hover" />
                    Identificação Cadastur
                </p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-cadastur-number">Número do Cadastur</label>
                        <input id="set-cadastur-number" data-testid="settings-cadastur-number-input" value={form.cadastur_number} onChange={set("cadastur_number")} className="field-input mt-1.5" placeholder="00.000000.00.0000-0" />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-cadastur-url">Link de consulta cadastral</label>
                        <input id="set-cadastur-url" data-testid="settings-cadastur-url-input" type="url" value={form.cadastur_url} onChange={set("cadastur_url")} className="field-input mt-1.5" placeholder="https://cadastur.turismo.gov.br/..." />
                    </div>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400">
                    O selo oficial do Cadastur só aparece no rodapé depois que o número de registro for cadastrado aqui. Enquanto estiver vazio, nada é exibido no site.
                </p>
            </div>

            <div className="mt-7">
                <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-instagram">
                    <Instagram className="h-3.5 w-3.5 text-gold-hover" />
                    Instagram oficial
                </label>
                <input
                    id="set-instagram"
                    data-testid="settings-instagram-input"
                    type="url"
                    value={form.instagram_url}
                    onChange={(e) => setForm((f) => ({ ...f, instagram_url: e.target.value }))}
                    className="field-input mt-1.5"
                    placeholder="https://instagram.com/destinoresorts"
                />
                <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400">
                    Enquanto este campo estiver vazio, o ícone do Instagram fica oculto no site. Ao salvar a URL oficial, o ícone passa a aparecer no rodapé abrindo em nova aba.
                </p>
            </div>

            <div className="mt-6">
                <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="set-alert-email">
                    <Mail className="h-3.5 w-3.5 text-gold-hover" />
                    E-mail para alertas de novos leads
                </label>
                <input
                    id="set-alert-email"
                    data-testid="settings-alert-email-input"
                    type="email"
                    value={form.alert_email}
                    onChange={(e) => setForm((f) => ({ ...f, alert_email: e.target.value }))}
                    className="field-input mt-1.5"
                    placeholder="contato@destinoresorts.com.br"
                />
                <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400">
                    Cada novo lead do formulário ou do consultor virtual dispara um resumo para este e-mail. Troque aqui quando o e-mail profissional estiver ativo.
                </p>
            </div>

            <button type="submit" data-testid="settings-save-btn" disabled={saving} className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest-emerald px-6 py-3 text-xs font-semibold text-white transition-colors hover:bg-forest-dark disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? "Salvando..." : "Salvar configurações"}
            </button>
        </form>
        <AdminPasswordCard />
        </>
    );
}

/** Card de troca de senha administrativa (renderizado abaixo das configurações). */
export function AdminPasswordCard() {
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [savingPwd, setSavingPwd] = useState(false);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (pwd.next.length < 8) {
            toast.error("A nova senha deve ter pelo menos 8 caracteres.");
            return;
        }
        if (pwd.next !== pwd.confirm) {
            toast.error("As senhas não conferem.");
            return;
        }
        setSavingPwd(true);
        try {
            await api.post("/api/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
            setPwd({ current: "", next: "", confirm: "" });
            toast.success("Senha alterada com sucesso! Guarde-a em local seguro.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Não foi possível alterar a senha.");
        } finally {
            setSavingPwd(false);
        }
    };

    return (
        <form onSubmit={handleChangePassword} className="mt-6 max-w-2xl rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm sm:p-8" data-testid="admin-password-card">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-forest-deep">
                <KeyRound className="h-5 w-5 text-gold-hover" />
                Alterar senha do painel
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-stone-500">
                Ao trocar a senha, todas as outras sessões são encerradas automaticamente. Mínimo de 8 caracteres.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="pwd-current">Senha atual</label>
                    <input id="pwd-current" data-testid="pwd-current-input" type="password" required value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))} className="field-input mt-1.5" autoComplete="current-password" />
                </div>
                <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="pwd-next">Nova senha</label>
                    <input id="pwd-next" data-testid="pwd-next-input" type="password" required minLength={8} value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))} className="field-input mt-1.5" autoComplete="new-password" />
                </div>
                <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="pwd-confirm">Confirmar nova senha</label>
                    <input id="pwd-confirm" data-testid="pwd-confirm-input" type="password" required minLength={8} value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} className="field-input mt-1.5" autoComplete="new-password" />
                </div>
            </div>

            <button type="submit" data-testid="pwd-save-btn" disabled={savingPwd} className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest-deep px-6 py-3 text-xs font-semibold text-white transition-colors hover:bg-forest-emerald disabled:opacity-50">
                <KeyRound className="h-4 w-4" />
                {savingPwd ? "Alterando..." : "Alterar senha"}
            </button>
        </form>
    );
}
